import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { getLLMRouter } from '../llm/llm-gate.js';
import { MAX_RULES_PER_CATEGORY } from '../../../shared/memory.js';
import { memoryAgentModel, memoryAgentTemperature } from '../../../shared/models.js';
import { Conversation } from '../../conversation.js';
import { Exam, type ExamState } from '../../exam.js';
import { ALL_SUBJECTS, isSubject, type Subject } from '../../subjects.js';
import { Teacher } from '../../teacher.js';
import { addCostToExam } from '../add-cost.js';

export const saveTeacherMemoryToolDef = [
  {
    type: 'function',
    function: {
      name: 'save_teacher_memory',
      description:
        'Persist a teacher preference, rule, or stylistic choice to long-term memory so it applies to their future exams and chats. ' +
        'If the teacher directly asks you for a specific behaviour or to remember a preference, you may save it directly. ' +
        'The teacher does not have to state his preferences directly, you can also infer them from the context. ' +
        'IMPORTANT: Do NOT call this on your own if you have inferred a preference. When you notice a possible durable preference (e.g. about grading/feedback, master solutions, grading schemes, or chat style), FIRST ask the teacher in your normal reply whether you should remember it for the future. ' +
        'Only call this tool AFTER the teacher has explicitly confirmed.\n' +
        'IMPORTANT: If the teacher is REVERTING or CANCELLING a behaviour they previously asked for (e.g. "grade normally again", "stop deducting for units", "go back to how it was", "forget that rule"), that is a DELETION, NOT a new rule. Do NOT save a counter-rule like "grade normally" — instead use delete_teacher_memory to remove the existing rule.',
      parameters: {
        type: 'object',
        properties: {
          memory_categories: {
            type: 'array',
            minItems: 1,
            items: {
              type: 'string',
              enum: ['chat_and_workflow', 'grading_and_feedback', 'master_solution_design', 'grading_scheme_creation'],
            },
            description:
              'Which area(s) this preference applies to — include every one it genuinely affects:\n' +
              "- 'chat_and_workflow': how YOU (the assistant) behave — your chat tone/format AND how you use your tools and drive the workflow (e.g. when to start grading, how to sequence steps, what to confirm or ask first). These apply to you directly.\n" +
              "- 'grading_and_feedback': how the GRADING step scores answers and writes its feedback/justifications (applied by the grading agent — NOT about when you trigger grading).\n" +
              "- 'master_solution_design': how the MASTER-SOLUTION generation step writes master solutions.\n" +
              "- 'grading_scheme_creation': how the GRADING-SCHEME generation step builds the scheme.\n" +
              "IMPORTANT: a preference about WHEN or HOW you invoke a tool / run the workflow belongs in 'chat_and_workflow', even when it is about grading, master solutions, or schemes.",
          },
          extracted_rule: {
            type: 'string',
            description:
              "The concise, actionable insight formulated as a rule. E.g., 'Deduct 1 point for missing units' or 'Keep chat replies to bullet points'. " +
              "ALWAYS formulate the rule in the teacher's language (the current conversation language), so it reads naturally when the teacher reviews and edits it in their profile.",
          },
          subject: {
            type: 'string',
            enum: ['general', ...ALL_SUBJECTS],
            description:
              "Whether the rule is general or subject-specific. Choose 'general' for a preference that applies across ALL subjects — this is the common case. " +
              'Choose a specific subject key ONLY when the preference clearly applies to that one subject (e.g. the teacher names the subject, or the current exam is in math and the rule is about calculations). When in doubt, choose general.',
          },
        },
        required: ['memory_categories', 'extracted_rule', 'subject'],
      },
    },
  },
];

export const getTeacherMemoryToolDef = [
  {
    type: 'function',
    function: {
      name: 'get_teacher_memory',
      description:
        "Look up the teacher's currently stored long-term preferences across ALL categories (grading & feedback, master solution, grading scheme, chat). " +
        'Call this ONLY when: (a) the teacher explicitly asks to see/review their saved preferences, or (b) you are about to refine or replace an existing preference and need to see the current rules first. ' +
        'Do NOT reveal the stored preferences to the teacher unless they explicitly asked to see them — for a refinement, read the result silently, craft the updated rule, save it via save_teacher_memory, then answer the user normally. ' +
        'Note: the grading / master-solution / grading-scheme preferences are applied automatically by their background steps and are not otherwise visible to you.',
      parameters: { type: 'object', properties: {}, required: [] },
    },
  },
];

export const deleteTeacherMemoryToolDef = [
  {
    type: 'function',
    function: {
      name: 'delete_teacher_memory',
      description:
        "Permanently delete the teacher's stored preferences. Two modes:\n" +
        "- DELETE ALL: set 'clear_all' to true to wipe every stored preference across all categories. This is destructive and irreversible — only do it AFTER you have shown the teacher their current preferences (via get_teacher_memory) and they have explicitly confirmed they want everything deleted.\n" +
        "- DELETE SPECIFIC: leave 'clear_all' false and describe in 'deletion_request' which behaviour(s) to remove (e.g. 'stop deducting points for missing units'). Matching rules are removed; the rest are kept.\n" +
        'In BOTH modes, only call this AFTER the teacher has explicitly asked to remove/forget a preference if he wants you to revert to normal behaviour, also delete the affected rule or all rules if general. Never delete on your own initiative. ' +
        'For DELETE SPECIFIC, ALWAYS call get_teacher_memory first and inspect the full list: the same behaviour may be stored as several rules (across different categories, and as subject-specific plus a general rule). Phrase your deletion_request so it covers ALL of those matching rules, not just one, so nothing related is left behind.',
      parameters: {
        type: 'object',
        properties: {
          clear_all: {
            type: 'boolean',
            description: 'true to delete ALL stored preferences across every category. Defaults to false.',
          },
          deletion_request: {
            type: 'string',
            description:
              'When clear_all is false: a concise natural-language description of which behaviour(s)/rule(s) to remove. Ignored when clear_all is true.',
          },
        },
        required: [],
      },
    },
  },
];

/** A field on the embedded {@link import('../../teacher.js').Memory} entity holding a list of rules. */
type MemoryField = 'gradingStyle' | 'chattingStyle' | 'masterSolutionStyle' | 'gradingSchemeStyle';

/** Sentinel `subject` value for a rule that applies across all subjects. */
const GENERAL = 'general' as const;

/** A rule's subject: a canonical subject key, or the explicit `'general'` sentinel. */
type RuleSubject = Subject | typeof GENERAL;

/** A single preference rule. `subject` is 'general' (all subjects) or a canonical subject key. */
interface RuleObj {
  rule: string;
  subject: RuleSubject;
}

interface MemoryCategoryConfig {
  /** Target field on the Memory entity. */
  field: MemoryField;
  /** Human-readable header used when rendering this category into the system prompt. */
  label: string;
}

/** Maps the tool's `memory_categories` enum values onto the persisted Memory fields. */
export const MEMORY_CATEGORY_CONFIG: Record<string, MemoryCategoryConfig> = {
  grading_and_feedback: { field: 'gradingStyle', label: 'Bewertung & Feedback' },
  chat_and_workflow: { field: 'chattingStyle', label: 'Chat & Workflow' },
  master_solution_design: { field: 'masterSolutionStyle', label: 'Musterlösungs-Erstellung' },
  grading_scheme_creation: { field: 'gradingSchemeStyle', label: 'Bewertungsschema-Erstellung' },
};

const MEMORY_FIELDS: MemoryField[] = ['gradingStyle', 'chattingStyle', 'masterSolutionStyle', 'gradingSchemeStyle'];

/** A `memory_category` enum value / key of {@link MEMORY_CATEGORY_CONFIG}. */
export type MemoryCategoryKey = keyof typeof MEMORY_CATEGORY_CONFIG;

/** Coerce a candidate subject into a canonical key, or the 'general' sentinel when absent/unknown. */
const normalizeRuleSubject = (value: unknown): RuleSubject => (isSubject(value) ? value : GENERAL);

/** Parse a legacy "[subject] text" string (older storage format) into a rule object. */
const parseLegacyRuleLine = (line: string): RuleObj => {
  const match = /^\s*\[([^\]]+)\]\s*(.*)$/.exec(line);
  if (match)
    return { rule: String(match[2]).trim(), subject: normalizeRuleSubject(String(match[1]).trim().toLowerCase()) };
  return { rule: line.trim(), subject: GENERAL };
};

/**
 * Tolerantly read a stored field into rule objects. Handles the current array-of-objects format as
 * well as legacy formats (array of tagged strings, or a single newline-separated tagged string).
 */
const readRules = (value: unknown): RuleObj[] => {
  const fromItem = (item: unknown): RuleObj | null => {
    if (item && typeof item === 'object') {
      const rule = String((item as any).rule ?? '').trim();
      return rule ? { rule, subject: normalizeRuleSubject((item as any).subject) } : null;
    }
    const raw = String(item ?? '').trim();
    return raw ? parseLegacyRuleLine(raw) : null;
  };

  if (Array.isArray(value)) return value.map(fromItem).filter((r): r is RuleObj => r !== null);
  if (typeof value === 'string') {
    return value
      .split('\n')
      .map(l => l.trim())
      .filter(Boolean)
      .map(parseLegacyRuleLine);
  }
  return [];
};

/** Plain document shape stored for each rule (matches the MemoryRule entity). */
const toStoredRule = (r: RuleObj) => ({ _class: 'MemoryRule', rule: r.rule, subject: r.subject });

/** Drop the oldest rules (front of the list) so a category never exceeds the cap. */
const capRules = (rules: RuleObj[]): RuleObj[] =>
  rules.length > MAX_RULES_PER_CATEGORY ? rules.slice(-MAX_RULES_PER_CATEGORY) : rules;

/**
 * Keep only rules relevant to one subject: 'general' rules always apply; a subject-specific rule
 * applies only when its subject matches. With no subject known, only 'general' rules apply.
 */
const filterRulesForSubject = (rules: RuleObj[], subject?: string | null): RuleObj[] => {
  const subj = subject?.trim().toLowerCase();
  return rules.filter(r => r.subject === GENERAL || (subj ? r.subject.toLowerCase() === subj : false));
};

/**
 * Resolve the owning teacher for a tool invocation. The tool runs with the lightweight exam context
 * built in the agent loop ({ _id, conversationId, examState }), so we re-load the real exam for its
 * userID and fall back to the conversation when no exam is linked (pure chat).
 */
export async function resolveTeacherUserId(exam: Exam): Promise<string | undefined> {
  const examId = (exam as any)?._id;
  const conversationId = (exam as any)?.conversationId;

  if (examId) {
    const examDoc = await (await getCollection(Exam)).findOne({ _id: examId });
    if (examDoc?.userID) return examDoc.userID;
  }
  if (conversationId) {
    const conversation = await (await getCollection(Conversation)).findOne({ _id: conversationId });
    const userID = (conversation as any)?.userID;
    if (userID) return userID;
  }
  return undefined;
}

/**
 * Conflict manager. Fires the lightweight model with the existing rules and the one new rule and
 * returns the curated rule list: the new rule wins on conflict, superseded/duplicate rules are
 * dropped, and a preference shared across several subjects collapses into one general rule. On any
 * failure it degrades gracefully (replace a same-text rule, otherwise append) so nothing is lost.
 */
async function curateRules(params: {
  categoryLabel: string;
  newRule: RuleObj;
  existingRules: RuleObj[];
  examId?: string;
}): Promise<RuleObj[]> {
  const { categoryLabel, newRule, existingRules, examId } = params;
  const fallback = (): RuleObj[] => [
    ...existingRules.filter(r => r.rule.toLowerCase() !== newRule.rule.toLowerCase()),
    newRule,
  ];

  const systemPrompt = `You are a memory-curation assistant for an AI that supports a single teacher. You maintain a concise, non-redundant set of preference rules for ONE category: "${categoryLabel}".

Each rule is an object { "subject": <"general"|subject-key>, "rule": <text> }:
- "subject": "general" means the rule applies across ALL subjects. This is the common case.
- "subject" set to a subject key means the rule applies ONLY to that subject.

You receive the EXISTING rules (oldest first) and exactly ONE NEW rule. Return the updated, complete set.

Principles:
- The NEW rule is the most recent signal and ALWAYS wins. If it conflicts with an existing rule, or is an updated / re-scoped version of the same preference — even when the subject differs (e.g. a previously "general" preference now restricted to one subject, or a subject-specific one now made general) — REMOVE the old rule and keep ONLY the NEW one. The new rule's subject/scope is authoritative.
- If the same or a clearly similar preference exists for several DIFFERENT subjects, merge them into ONE "general" rule.
- Never drop information that is still valid. Only remove a rule when the new rule replaces/conflicts with it, or it is a duplicate.
- Keep every "rule" a single, concise, actionable sentence. Do not invent rules that were not stated.
- Preserve the original language of the rule text.
- ORDER the returned rules oldest first and put the new/updated rule LAST.

Allowed "subject" values: "general", or one of: ${ALL_SUBJECTS.join(', ')}.

Return ONLY JSON: { "rules": [ { "subject": <"general"|subject-key>, "rule": <text> }, ... ] }. No commentary, no markdown.`;

  const userPrompt = `CATEGORY: ${categoryLabel}

EXISTING RULES (JSON, oldest first):
${existingRules.length ? JSON.stringify(existingRules) : '[]'}

NEW RULE (JSON):
${JSON.stringify(newRule)}`;

  try {
    const router = getLLMRouter({}, 'interactive');
    const response = await router.chat.completions.create({
      model: memoryAgentModel,
      provider: 'openrouter',
      temperature: memoryAgentTemperature,
      response_format: { type: 'json_object' },
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ] as any,
    });

    await addCostToExam(response, examId, 'memoryCurateRule');

    const content = response.choices[0]?.message?.content;
    if (!content) return fallback();

    const parsed = JSON.parse(content);
    const rules: unknown = Array.isArray(parsed) ? parsed : parsed?.rules;
    if (!Array.isArray(rules)) return fallback();

    const cleaned: RuleObj[] = rules
      .map(item => {
        const rule = String((item as any)?.rule ?? '')
          .replace(/\s*\n+\s*/g, ' ')
          .trim();
        return rule ? { rule, subject: normalizeRuleSubject((item as any)?.subject) } : null;
      })
      .filter((r): r is RuleObj => r !== null);

    return cleaned.length ? cleaned : fallback();
  } catch (err) {
    logger.warn({ err, categoryLabel }, 'Teacher memory curation failed; falling back to plain append');
    return fallback();
  }
}

export async function handleSaveTeacherMemory(exam: Exam, toolCallArgs: any): Promise<object> {
  // Accept the array param, tolerate a single string for safety; keep only known, deduped categories.
  const rawCategories: unknown[] = Array.isArray(toolCallArgs?.memory_categories)
    ? toolCallArgs.memory_categories
    : toolCallArgs?.memory_category != null
      ? [toolCallArgs.memory_category]
      : [];
  const categories = [...new Set(rawCategories.map(c => String(c ?? '')))].filter(c =>
    Boolean(MEMORY_CATEGORY_CONFIG[c]),
  );
  const rawRule = String(toolCallArgs?.extracted_rule ?? '').trim();
  // The agent indicates subject specificity explicitly: a valid key → that subject, otherwise 'general'.
  const subject = normalizeRuleSubject(toolCallArgs?.subject);

  if (!categories.length || !rawRule) {
    return {
      content:
        'SYSTEM INFO: Die Präferenz konnte nicht gespeichert werden (unbekannte Kategorie oder leere Regel). Ignoriere dies und fahre normal fort.',
    };
  }

  const userID = await resolveTeacherUserId(exam);
  if (!userID) {
    return {
      content:
        'SYSTEM INFO: Mit diesem Chat ist keine Lehrkraft verknüpft, daher wurde die Präferenz nicht dauerhaft gespeichert. Fahre normal fort.',
    };
  }

  const teacherCollection = await getCollection(Teacher);
  const teacher = await teacherCollection.findOne({ userID });
  if (!teacher) {
    return {
      content:
        'SYSTEM INFO: Für diesen Nutzer existiert kein persistentes Lehrkraft-Profil; die Präferenz wurde nicht dauerhaft gespeichert. Fahre normal fort.',
    };
  }

  const newRule: RuleObj = { rule: rawRule, subject };
  const baseMemory = (teacher.memory ?? {}) as Record<string, unknown>;

  // Each category maps to a distinct field, so the curation calls are independent and run in
  // parallel; all results are written back in a single update.
  const updatedFields = await Promise.all(
    categories.map(async category => {
      const config = MEMORY_CATEGORY_CONFIG[category]!;
      const curated = await curateRules({
        categoryLabel: config.label,
        newRule,
        existingRules: readRules(baseMemory[config.field]),
        examId: (exam as any)?._id,
      });
      const rules = capRules(curated);
      return { field: config.field, rules };
    }),
  );

  // Rebuild the full memory doc: untouched fields are normalized from existing data, changed fields
  // are overwritten with the curated+capped lists.
  const newMemory: Record<string, unknown> = {
    _class: 'Memory',
    ...(baseMemory._id ? { _id: baseMemory._id } : {}),
  };
  for (const field of MEMORY_FIELDS) {
    newMemory[field] = readRules(baseMemory[field]).map(toStoredRule);
  }
  for (const { field, rules } of updatedFields) {
    newMemory[field] = rules.map(toStoredRule);
  }

  await teacherCollection.updateOne({ userID }, { $set: { memory: newMemory } } as any);

  logger.info(
    {
      userID,
      categories,
      subject,
      ruleCounts: updatedFields.map(f => `${f.field}:${f.rules.length}`),
    },
    'Teacher memory updated',
  );

  return {
    content:
      'SYSTEM INFO: Die Präferenz der Lehrkraft wurde gespeichert und mit den bestehenden Regeln abgeglichen. Bestätige der Lehrkraft kurz, dass du es dir für die Zukunft gemerkt hast. Weise sie außerdem darauf hin, dass sie ihre gespeicherten Regeln jederzeit selbst im Profil-Tab einsehen und bearbeiten kann aber dass du sie auch auf Wunsch jederzeit anpassen oder entfernen kannst.',
  };
}

export function saveTeacherMemoryAvailability(_examState: ExamState): boolean {
  return true;
}

/**
 * Return the teacher's full stored preferences (all categories) for the main agent's INTERNAL use —
 * reviewing on explicit request, or as the lookup step before refining/replacing a rule. The result
 * is informational for the agent (it must not surface it unless the teacher explicitly asks) and
 * carries curation guidance so refinements are phrased as replacements.
 */
export async function handleGetTeacherMemory(exam: Exam): Promise<object> {
  const userID = await resolveTeacherUserId(exam);
  if (!userID) {
    return {
      content:
        'SYSTEM INFO: Mit diesem Chat ist keine Lehrkraft verknüpft; es sind keine gespeicherten Präferenzen verfügbar.',
    };
  }

  const teacher = await (await getCollection(Teacher)).findOne({ userID });
  const memory = teacher?.memory;

  const sections: string[] = [];
  for (const [category, config] of Object.entries(MEMORY_CATEGORY_CONFIG)) {
    const rules = readRules((memory as any)?.[config.field]);
    if (!rules.length) continue;
    const body = rules.map(r => (r.subject === GENERAL ? `- ${r.rule}` : `- [${r.subject}] ${r.rule}`)).join('\n');
    sections.push(`**${config.label}** (category: "${category}"):\n${body}`);
  }

  logger.info({ userID, categoryCount: sections.length }, 'get_teacher_memory called');

  if (!sections.length) {
    return { content: 'SYSTEM INFO: Für diese Lehrkraft sind noch keine Präferenzen gespeichert.' };
  }

  return {
    content:
      'SYSTEM INFO — Gespeicherte Präferenzen der Lehrkraft (nur zu deiner internen Nutzung; gib sie dem Nutzer NUR aus, wenn er ausdrücklich danach fragt):\n\n' +
      sections.join('\n\n') +
      '\n\nZum Verfeinern/Ersetzen: Speichere die verbesserte Formulierung über save_teacher_memory mit der/den passenden Kategorie(n) und subject. Das System gleicht automatisch ab — widersprüchliche/veraltete Regeln werden ersetzt, ähnliche zusammengeführt, pro Kategorie max. ~10 (älteste entfallen). Zum Löschen einer oder aller Präferenzen nutze delete_teacher_memory (nur auf ausdrücklichen Wunsch der Lehrkraft).',
  };
}

export function getTeacherMemoryAvailability(_examState: ExamState): boolean {
  return true;
}

/**
 * Deletion selector. Fires the lightweight model with one category's existing rules and the
 * teacher's natural-language deletion request, and returns the rules to KEEP. On any failure it
 * degrades to keeping everything (so we never delete the wrong rules on a parse error).
 */
async function selectRulesToKeep(params: {
  categoryLabel: string;
  deletionRequest: string;
  existingRules: RuleObj[];
  examId?: string;
}): Promise<RuleObj[]> {
  const { categoryLabel, deletionRequest, existingRules, examId } = params;
  if (!existingRules.length) return [];

  const systemPrompt = `You are a memory-curation assistant for an AI that supports a single teacher. You maintain preference rules for ONE category: "${categoryLabel}".

You receive the EXISTING rules (oldest first) and a natural-language DELETION REQUEST from the teacher. Return the rules that should be KEPT.

Principles:
- Remove ONLY rules that clearly match the teacher's deletion request. Keep everything else, unchanged and in the same order.
- If no existing rule matches the request, return all existing rules unchanged.
- Never invent, reword, or merge rules. Return the kept rules exactly as given.

Each rule is an object { "subject": <"general"|subject-key>, "rule": <text> }.

Return ONLY JSON: { "rules": [ { "subject": ..., "rule": ... }, ... ] }. No commentary, no markdown.`;

  const userPrompt = `CATEGORY: ${categoryLabel}

EXISTING RULES (JSON, oldest first):
${JSON.stringify(existingRules)}

DELETION REQUEST:
${deletionRequest}`;

  try {
    const router = getLLMRouter({}, 'interactive');
    const response = await router.chat.completions.create({
      model: memoryAgentModel,
      provider: 'openrouter',
      temperature: memoryAgentTemperature,
      response_format: { type: 'json_object' },
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ] as any,
    });

    await addCostToExam(response, examId, 'memoryDeleteSelect');

    const content = response.choices[0]?.message?.content;
    if (!content) return existingRules;

    const parsed = JSON.parse(content);
    const rules: unknown = Array.isArray(parsed) ? parsed : parsed?.rules;
    if (!Array.isArray(rules)) return existingRules;

    return rules
      .map(item => {
        const rule = String((item as any)?.rule ?? '').trim();
        return rule ? { rule, subject: normalizeRuleSubject((item as any)?.subject) } : null;
      })
      .filter((r): r is RuleObj => r !== null);
  } catch (err) {
    logger.warn({ err, categoryLabel }, 'Teacher memory deletion-selection failed; keeping all rules');
    return existingRules;
  }
}

/**
 * Delete teacher preferences. With `clear_all`, wipes every category. Otherwise uses the lightweight
 * model to drop only the rules matching the teacher's natural-language `deletion_request`, per
 * category. Only ever called after the teacher explicitly asked to remove a preference.
 */
export async function handleDeleteTeacherMemory(exam: Exam, toolCallArgs: any): Promise<object> {
  const clearAll = toolCallArgs?.clear_all === true;
  const deletionRequest = String(toolCallArgs?.deletion_request ?? '').trim();

  const userID = await resolveTeacherUserId(exam);
  if (!userID) {
    return {
      content:
        'SYSTEM INFO: Mit diesem Chat ist keine Lehrkraft verknüpft; es konnten keine Präferenzen gelöscht werden. Fahre normal fort.',
    };
  }

  const teacherCollection = await getCollection(Teacher);
  const teacher = await teacherCollection.findOne({ userID });
  if (!teacher) {
    return {
      content:
        'SYSTEM INFO: Für diesen Nutzer existiert kein persistentes Lehrkraft-Profil; es konnten keine Präferenzen gelöscht werden. Fahre normal fort.',
    };
  }

  const baseMemory = (teacher.memory ?? {}) as Record<string, unknown>;

  if (clearAll) {
    const newMemory: Record<string, unknown> = {
      _class: 'Memory',
      ...(baseMemory._id ? { _id: baseMemory._id } : {}),
    };
    for (const field of MEMORY_FIELDS) newMemory[field] = [];
    await teacherCollection.updateOne({ userID }, { $set: { memory: newMemory } } as any);
    logger.info({ userID }, 'Teacher memory cleared (all categories)');
    return {
      content:
        'SYSTEM INFO: Alle gespeicherten Präferenzen dieser Lehrkraft wurden gelöscht. Bestätige der Lehrkraft kurz, dass du alle gemerkten Präferenzen entfernt hast.',
    };
  }

  if (!deletionRequest) {
    return {
      content:
        'SYSTEM INFO: Es wurde keine Beschreibung angegeben, welche Präferenz gelöscht werden soll. Frage die Lehrkraft, was genau entfernt werden soll.',
    };
  }

  // Run the deletion selection per category in parallel; write all kept lists back in one update.
  const keptFields = await Promise.all(
    MEMORY_FIELDS.map(async field => {
      const existingRules = readRules(baseMemory[field]);
      if (!existingRules.length) return { field, rules: existingRules, removed: 0 };
      const label = Object.values(MEMORY_CATEGORY_CONFIG).find(c => c.field === field)?.label ?? field;
      const kept = await selectRulesToKeep({
        categoryLabel: label,
        deletionRequest,
        existingRules,
        examId: (exam as any)?._id,
      });
      return { field, rules: kept, removed: existingRules.length - kept.length };
    }),
  );

  const totalRemoved = keptFields.reduce((sum, f) => sum + Math.max(0, f.removed), 0);

  const newMemory: Record<string, unknown> = {
    _class: 'Memory',
    ...(baseMemory._id ? { _id: baseMemory._id } : {}),
  };
  for (const { field, rules } of keptFields) newMemory[field] = rules.map(toStoredRule);

  await teacherCollection.updateOne({ userID }, { $set: { memory: newMemory } } as any);
  logger.info({ userID, totalRemoved }, 'Teacher memory specific deletion applied');

  if (totalRemoved === 0) {
    return {
      content:
        'SYSTEM INFO: Es wurde keine passende Präferenz gefunden, die zu dieser Löschanfrage passt; es wurde nichts entfernt. Teile der Lehrkraft mit, dass du keine entsprechende gespeicherte Präferenz finden konntest.',
    };
  }

  return {
    content:
      'SYSTEM INFO: Die passende(n) Präferenz(en) wurde(n) gelöscht. Bestätige der Lehrkraft kurz, dass du die gewünschte Präferenz entfernt hast.',
  };
}

export function deleteTeacherMemoryAvailability(_examState: ExamState): boolean {
  return true;
}

/**
 * Build the chat-style personalization injected into the main agent's system prompt on every turn.
 * ONLY the `chat_and_workflow` rules live here — they govern how the agent itself replies and uses
 * its tools / drives the workflow, so they must always be present (subject-filtered). The grading/master-solution/grading-scheme preferences
 * are deliberately NOT loaded here: they apply in their background substeps, and the agent fetches
 * the full set on demand via get_teacher_memory. Returns '' when there are no matching chat rules.
 */
export async function buildTeacherMemoryPromptSection(
  userID?: string | null,
  subject?: string | null,
): Promise<string> {
  if (!userID) return '';

  try {
    const teacher = await (await getCollection(Teacher)).findOne({ userID });
    const rules = filterRulesForSubject(readRules((teacher?.memory as any)?.chattingStyle), subject);
    if (!rules.length) return '';

    logger.info({ userID, subject, ruleCount: rules.length }, 'Loaded teacher chat preferences for prompt');

    const body = rules.map(r => `- ${r.rule}`).join('\n');
    return `### PERSÖNLICHE CHAT- & WORKFLOW-PRÄFERENZEN DIESER LEHRKRAFT
Dies sind dauerhaft gespeicherte Präferenzen dafür, WIE du im Chat antwortest und deine Tools / den Arbeitsablauf nutzt. Sie haben VORRANG vor deinem Standardverhalten — richte dich bei allen Antworten und Tool-Aufrufen danach.
${body}`;
  } catch (err) {
    logger.error({ err, userID }, 'Failed to build teacher chat preferences section');
    return '';
  }
}

/**
 * Build the teacher-preferences block injected into a single generation/grading sub-agent prompt
 * (master solution, grading scheme, grading). Loads one category's rules, keeps only those relevant
 * to the exam's subject (general + subject-matching), and renders the rule text (no tag, since the
 * context is a single subject). Returns '' when there is nothing to inject.
 */
export async function getTeacherMemoryInstructionBlock(params: {
  userID?: string | null;
  category: MemoryCategoryKey;
  subject?: string | null;
}): Promise<string> {
  const { userID, category, subject } = params;
  const config = MEMORY_CATEGORY_CONFIG[category];

  try {
    let rules: RuleObj[] = [];
    if (userID && config) {
      const teacher = await (await getCollection(Teacher)).findOne({ userID });
      rules = filterRulesForSubject(readRules((teacher?.memory as any)?.[config.field]), subject);
    }

    logger.info(
      { userID: userID ?? null, category, subject: subject ?? null, ruleCount: rules.length },
      'Loaded teacher memory for sub-agent prompt',
    );

    if (!rules.length) return '';

    const body = rules.map(r => `- ${r.rule}`).join('\n');
    logger.info(
      { userID, category, subject, ruleCount: rules.length },
      'Built teacher memory instruction block' + `:\n${body}`,
    );
    return `\n--- TEACHER PREFERENCES (HIGH PRIORITY) ---\nThese are explicit, persistent preferences of THIS teacher. They take PRIORITY over the default instructions whenever they conflict — follow them. (The only thing a preference must never override is objective correctness.)\n${body}\n-------------------------------------------\n`;
  } catch (err) {
    logger.error({ err, userID, category }, 'Failed to build teacher memory instruction block');
    return '';
  }
}
